import { API } from '../constants/constants';
import { normalizeServiceId } from '../utils/serviceLogNormalization';

const toIdArray = (value) => {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.map((item) => normalizeServiceId(item)).filter(Boolean);
};

const sanitizeTypeEntries = (value) => {
  if (!Array.isArray(value)) {
    return value;
  }
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const { name, type, backgroundColor, permissions, proactivity } = entry;
      if (!name || !type) {
        return null;
      }
      return {
        name,
        type,
        backgroundColor,
        permissions,
        proactivity,
      };
    })
    .filter(Boolean);
};

const sanitizeCampaignEntries = (value) => {
  if (!Array.isArray(value)) {
    return value;
  }
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const { name, backgroundColor } = entry;
      if (!name) {
        return null;
      }
      return {
        name,
        backgroundColor,
      };
    })
    .filter(Boolean);
};

const normalizeServiceFields = (fields) => {
  if (!fields || typeof fields !== 'object') {
    return {};
  }
  const normalizedFields = { ...fields };
  const linkedInteraction = normalizedFields.linkedInteraction?.interactions
    ? normalizedFields.linkedInteraction.interactions
    : normalizedFields.linkedInteraction;
  const linkedEngagement = normalizedFields.linkedEngagement?.engagements
    ? normalizedFields.linkedEngagement.engagements
    : normalizedFields.linkedEngagement;

  normalizedFields.linkedInteraction = toIdArray(linkedInteraction);
  normalizedFields.linkedEngagement = toIdArray(linkedEngagement);
  if (normalizedFields.unitLead) {
    normalizedFields.unitLead = toIdArray(normalizedFields.unitLead);
  }
  if (normalizedFields.teamMember) {
    normalizedFields.teamMember = toIdArray(normalizedFields.teamMember);
  }
  if (normalizedFields.type) {
    normalizedFields.type = sanitizeTypeEntries(normalizedFields.type);
  }
  if (normalizedFields.campaign) {
    normalizedFields.campaign = sanitizeCampaignEntries(normalizedFields.campaign);
  }
  delete normalizedFields.year;
  delete normalizedFields.month;

  return normalizedFields;
};

const normalizeUrlPayload = (file) => {
  if (!file || typeof file !== 'object') {
    return null;
  }
  const url = typeof file.url === 'string' && file.url.trim() ? file.url.trim() : file.path;
  if (!url) return null;
  return {
    url,
    name: typeof file.name === 'string' ? file.name : '',
    description: typeof file.description === 'string' ? file.description : '',
  };
};

export const getServiceById = async (id) => {
  const normalizedId = normalizeServiceId(id);
  if (!normalizedId) {
    return { error: 1, message: 'invalid service id' };
  }
  try {
    const response = await API.get(`/services/${normalizedId}`);
    if (response.status === 204) {
      return 'noData';
    }
    return response.data;
  } catch (e) {
    return { error: 1, message: e.message };
  }
};

export const getMonthlyServices = async (month, timezoneOffset, isCalendarEvent = false) => {
  const normalizedMonth = typeof month === 'string' ? month : month?.toString?.();
  if (!normalizedMonth) {
    return [];
  }
  const res = await API.get('/services/monthly', {
    params: {
      month: normalizedMonth,
      timezoneOffset,
      isCalendarEvent: Boolean(isCalendarEvent),
    },
  });
  return res.data;
};

export const createService = async (fields) => {
  const normalizedFields = normalizeServiceFields(fields);
  const res = await API.post(`/services`, [normalizedFields]);
  return res;
};

export const uploadServiceFiles = async ({ id, files }) => {
  const normalizedId = normalizeServiceId(id);
  if (!normalizedId) {
    throw new Error('invalid service id');
  }
  const formData = new FormData();
  (files || [])
    .filter((file) => file.type !== 'url' && file.type !== 'autotrackerAttachment')
    .forEach((file) => formData.append('file', file));

  return API.post(`/service/${normalizedId}/upload`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
};

export const uploadServiceUrl = async ({ id, file }) => {
  const normalizedId = normalizeServiceId(id);
  const normalizedPayload = normalizeUrlPayload(file);
  if (!normalizedId || !normalizedPayload) {
    throw new Error('invalid service url payload');
  }
  return API.post(`/service/${normalizedId}/url`, normalizedPayload);
};

export const updateServiceWithFiles = async ({
  id,
  fields,
  uploadingFiles,
  uploadingUrls,
  removingFiles,
  removingUrls,
}) => {
  const normalizedId = normalizeServiceId(id);
  if (!normalizedId) {
    throw new Error('invalid service id');
  }
  const formData = new FormData();

  for (const file of uploadingFiles || []) {
    formData.append('file', file);
  }
  formData.append('urls', JSON.stringify(uploadingUrls || []));
  formData.append('deletingFiles', JSON.stringify(removingFiles || []));
  formData.append('deletingUrls', JSON.stringify(removingUrls || []));

  const normalizedFields = normalizeServiceFields(fields);

  formData.append('fields', JSON.stringify(normalizedFields));

  const res = await API.put(`/services/${normalizedId}/update`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return res.data;
};

export const deleteService = async (id) => {
  const normalizedId = normalizeServiceId(id);
  if (!normalizedId) {
    throw new Error('invalid service id');
  }
  const res = await API.delete(`/services/${normalizedId}`);
  return res.data;
};

export const getFilteredServices = async (filterQueryString) => {
  const query = typeof filterQueryString === 'string' ? filterQueryString : '';
  if (!query) {
    return [];
  }
  const res = await API.get(`/services/filter?${query}`);
  return res.data?.data;
};

export const getFuzzySearchServices = async (queryString, category) => {
  const normalizedQuery = typeof queryString === 'string' ? queryString : '';
  const normalizedCategory = typeof category === 'string' ? category : '';
  const res = await API.get(
    `/services/fuzzySearch?queryString=${encodeURIComponent(normalizedQuery)}&category=${encodeURIComponent(
      normalizedCategory
    )}`
  );
  return res.data?.data;
};
